<template>
    <div :id="`comment-${comment.id}`" class="space-y-2 relative">
        <!-- Header -->
        <div
            :class="[
                'flex justify-between items-center gap-x-2 relative',
                { 'pt-1': comment.deleted_at != null },
            ]"
        >
            <!-- User info -->
            <div
                :class="[
                    'flex items-center gap-x-2',

                    {
                        'after:content-[\'\'] after:block after:absolute after:-top-[calc(theme(spacing.2))] after:left-[calc(((theme(spacing.8)+theme(margin.1))*-1)-1.6px)] after:h-[calc(50%+theme(spacing.2))] after:border-border after:border-l after:border-b after:w-[calc(theme(spacing.8))] after:rounded-bl-xl':
                            comment.interaction_id != null,
                    },
                ]"
            >
                <ModelImage
                    class="size-10"
                    :src="comment?.user_image"
                    modelClass="user"
                    :alt="comment?.user?.name"
                    imageClasses="size-full aspect-square rounded-full"
                    :clickable="false"
                    :useAccentBg="true"
                />
                <div v-if="comment.deleted_at == null">
                    <Link
                        :href="
                            this.$getModelUrl({
                                modelClass: 'user',
                                id: comment.user.id,
                            })
                        "
                        v-text="comment?.user?.name"
                    />

                    <div class="flex items-center gap-x-2">
                        <RealtimeTimestamp
                            :timestamp="comment.created_at"
                            class="text-text-secondary"
                        />
                        <Transition name="fade" mode="out-in">
                            <p
                                v-if="isEdited"
                                class="flex items-center text-text-muted text-xs italic pt-0.5"
                            >
                                (
                                <span
                                    v-text="
                                        this.$lang.get('Edited :time_ago', {
                                            time_ago: '',
                                        })
                                    "
                                    class="pr-1"
                                />
                                <RealtimeTimestamp
                                    :timestamp="comment.updated_at"
                                />
                                )
                            </p>
                        </Transition>
                    </div>
                </div>
            </div>
            <!-- Options -->
            <ButtonPopoverMenu
                :items="options"
                v-if="canModerateComment && comment.deleted_at == null"
            >
                <template #button="{ open }">
                    <div
                        :class="[
                            open
                                ? `border-${modelClass}-accent bg-${modelClass}-accent text-${modelClass}`
                                : 'border-transparent',
                            `border hover:border-${modelClass}-accent size-12 p-2 rounded-full transition duration-button-hover focus-style`,
                        ]"
                    >
                        <PhDotsThreeOutline
                            :class="['flex-grow']"
                            weight="fill"
                        />
                    </div>
                </template>
            </ButtonPopoverMenu>
        </div>
        <div
            :class="[
                'pl-12',
                {
                    'after:content-[\'\'] after:block after:absolute after:top-[theme(spacing.4)] after:-left-[calc(theme(spacing.9)+1.6px)] after:h-[calc(100%+theme(spacing.2))] after:border-border after:border-l':
                        hasSucceedingSiblings,
                },
            ]"
        >
            <!-- Body -->
            <div
                :class="[
                    'relative',
                    {
                        '-mt-[calc(theme(spacing.10)+theme(spacing.3))]':
                            comment.deleted_at != null,
                    },
                ]"
            >
                <div
                    :class="[
                        'w-full break-words rounded-input border border-border/40 bg-white py-3 p-input',
                        highlightCommentId != null &&
                        highlightCommentId == comment.id
                            ? `ring-[6px] ring-${modelClass}-accent`
                            : '',
                        {
                            'after:content-[\'\'] after:block after:absolute after:-top-[calc(theme(spacing.2))] after:-left-[calc(theme(margin.7)+0.1rem)] after:h-[calc(100%+theme(spacing.4)+1px)] after:border-border after:border-l':
                                childComments.length &&
                                comment.deleted_at == null,
                        },
                    ]"
                >
                    <!-- <VerticalSlideTransition :show="comment.deleted_at == null"></VerticalSlideTransition> -->
                    <ReactiveHeightTransition duration="200ms">
                        <!-- Edit Comment -->
                        <p
                            v-if="comment.deleted_at != null"
                            v-text="$lang.get('This comment has been deleted')"
                            class="text-text-muted italic"
                        />
                        <Transition name="fade" mode="out-in">
                            <div v-if="editing" class="relative px-0.5">
                                <div
                                    class="flex items-center justify-between mb-1"
                                >
                                    <p v-text="$lang.get('Edit comment')" />
                                    <button
                                        v-text="$lang.get('Cancel')"
                                        @click="toggleEditing"
                                        class="underline-hover"
                                    />
                                </div>
                                <TextAreaInput
                                    @keydown.enter="handleEnterEdit"
                                    v-model="commentEditValue"
                                    ref="textareaEdit"
                                    :disabled="updating"
                                    :placeholder="
                                        $lang.get('Write your reply...')
                                    "
                                    :autoAdjustHeight="true"
                                />

                                <PrimaryButton
                                    :disabled="!canUpdate"
                                    size="xs"
                                    class="absolute right-2.5 bottom-5 mb-0.5 rounded-full p-2"
                                    @click="updateComment"
                                >
                                    <PhCheck class="size-6" weight="bold" />
                                </PrimaryButton>
                            </div>
                            <!-- Comment -->
                            <p
                                v-else
                                :key="comment.notes"
                                v-html="comment.notes"
                                class="[&>a]:underline [&>a]:decoration-text/30 [&>a]:underline-offset-4 [&>a]:transition-colors [&>a]:duration-text-hover [&>a]:text-link [&>a]:hover:decoration-text [&>a]:hover:text-link-hover"
                            />
                        </Transition>
                    </ReactiveHeightTransition>

                    <VerticalSlideTransition :show="comment.deleted_at == null">
                        <div class="w-full pt-4 flex gap-x-3">
                            <button
                                class="font-header font-medium underline-hover w-16"
                                v-text="
                                    replying
                                        ? $lang.get('Cancel')
                                        : $lang.get('Reply')
                                "
                                @click="toggleReplying"
                            />
                            <div
                                class="flex-grow my-[2px] max-w-px bg-border"
                            />
                            <Emotions
                                :initialEmotions="comment.emotions"
                                :availableEmotions="availableEmotions"
                                :modelId="comment.id"
                                modelClass="interaction"
                                :rootModelClass="modelClass"
                                :rootModelId="modelId"
                                :isCommentEmotions="true"
                            />
                        </div>
                    </VerticalSlideTransition>
                    <VerticalSlideTransition :show="replying">
                        <div class="mt-2 pt-4 border-t border-border">
                            <div class="relative">
                                <TextAreaInput
                                    ref="textareaReply"
                                    @keydown.enter="handleEnterPress"
                                    @keyup="
                                        whisper(
                                            $page.props.auth.user.id,
                                            this.$page.props.auth.user.name,
                                            { commentId: comment.id }
                                        )
                                    "
                                    :disabled="savingReply"
                                    v-model="commentBody"
                                    :placeholder="
                                        $lang.get('Write your reply...')
                                    "
                                    :autoAdjustHeight="true"
                                />

                                <div
                                    class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                                >
                                    <PhCircleNotch
                                        v-if="savingReply"
                                        class="size-12 animate-spin text-primary"
                                    />
                                </div>
                                <PrimaryButton
                                    :disabled="!canReply"
                                    size="xs"
                                    class="absolute right-1.5 bottom-5 rounded-full p-2"
                                    @click="addReply"
                                >
                                    <PhPaperPlaneTilt class="size-6" />
                                </PrimaryButton>
                            </div>
                            <InputError :message="errors?.comment" />
                        </div>
                    </VerticalSlideTransition>
                </div>
                <VerticalSlideTransition :show="usersTypingText != null">
                    <Transition name="fade" mode="out-in">
                        <WhisperText
                            v-if="usersTypingText"
                            :whisperText="usersTypingText"
                            class="h-5 mt-2 text-text-muted italic"
                        />
                    </Transition>
                </VerticalSlideTransition>
            </div>
            <ConfirmDangerModal ref="confirmDanger" />

            <div
                :class="[
                    { 'space-y-8 pt-4 pl-2': childComments.length > 0 },
                    comment.interaction_id == null ? bottomPaddingClass : '',
                ]"
            >
                <TransitionGroup
                    enter-active-class="transition-[transform,opacity] duration-500"
                    move-class="transition-all duration-500"
                    enter-from-class="scale-0 opacity-0"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-all duration-500"
                    leave-from-class="opacity-100 scale-100 max-h-[400px]"
                    leave-to-class="opacity-0 scale-0 max-h-0"
                >
                    <Comment
                        v-for="(childComment, index) in childComments"
                        @removed="childCommentRemoved"
                        :key="childComment.id"
                        :initialComment="childComment"
                        :availableEmotions="availableEmotions"
                        :modelId="modelId"
                        :modelClass="modelClass"
                        :hasSucceedingSiblings="
                            index !== childComments.length - 1
                        "
                        :highlightCommentId="highlightCommentId"
                    />
                </TransitionGroup>
            </div>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue'
import {
    PhX,
    PhDotsThreeOutline,
    PhPaperPlaneTilt,
    PhCircleNotch,
    PhCheck,
} from '@phosphor-icons/vue'
import { Link } from '@inertiajs/vue3'
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import RealtimeTimestamp from '@/Components/Utilities/RealtimeTimestamp.vue'
import ButtonPopoverMenu from '@/Components/Utilities/ButtonPopoverMenu.vue'
import Emotions from '@/Components/ModelComponents/Interactions/Emotions.vue'
import TextAreaInput from '@/Components/FormElements/TextAreaInput.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import ConfirmDangerModal from '@/Components/Modals/ConfirmDangerModal.vue'
import broadcastMixin from '@/Mixins/broadcastMixin.js'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import InputError from '@/Components/FormElements/InputError.vue'
import WhisperText from '@/Components/Chat/WhisperText.vue'
export default {
    name: 'Comment',

    components: {
        Comment: defineAsyncComponent(() =>
            import('@/Components/ModelComponents/Interactions/Comment.vue')
        ),
        ModelImage,
        RealtimeTimestamp,
        ButtonPopoverMenu,
        PhDotsThreeOutline,
        PhPaperPlaneTilt,
        PhCircleNotch,
        PhCheck,
        Emotions,
        TextAreaInput,
        VerticalSlideTransition,
        PrimaryButton,
        ConfirmDangerModal,
        ReactiveHeightTransition,
        Link,
        InputError,
        WhisperText,
    },

    mixins: [broadcastMixin],

    emits: ['removed'],

    props: {
        initialComment: {
            type: Object,
        },
        availableEmotions: {
            type: Array,
            required: true,
        },
        modelId: {
            type: Number,
            required: true,
        },
        modelClass: {
            type: String,
            required: true,
        },
        hasSucceedingSiblings: {
            type: Boolean,
            default: false,
        },
        highlightCommentId: {
            type: Number,
            default: null,
        },
        bottomPaddingClass: {
            type: String,
            default: 'pb-8',
        },
    },

    data() {
        return {
            // Comment
            comment: this.initialComment,
            childComments: this.initialComment?.comments || [],

            // InputVariables
            replying: false,
            commentBody: '',
            savingReply: false,
            editing: false,
            commentEditValue: '',
            updating: false,
            errors: null,

            // Options for the popover menu
            options: [
                {
                    icon: markRaw(PhX),
                    text: this.$lang.get('Remove comment'),
                    type: 'button',
                    action: this.deleteComment,
                },
                {
                    text: this.$lang.get('Edit comment'),
                    type: 'button',
                    action: this.toggleEditing,
                },
            ],
        }
    },

    mounted() {
        this.initEcho()
    },

    beforeUnmount() {
        this.destroyEcho()
    },

    computed: {
        canReply() {
            return this.commentBody.length >= 2 && !this.savingReply
        },

        canUpdate() {
            return this.commentEditValue.length >= 2 && !this.updating
        },

        isEdited() {
            return this.comment.updated_at > this.comment.created_at
        },

        usersTypingText() {
            if (this.typingUsers.length == 1) {
                return this.$lang.get(':name is writing a comment', {
                    name: this.typingUsers[0].name,
                })
            } else if (this.typingUsers.length > 0) {
                return this.$lang.get(
                    ':nbrTyping others are writing comments',
                    {
                        nbrTyping: this.typingUsers.length,
                    }
                )
            }
            return null
        },

        canModerateComment() {
            return (
                this.comment.user_id == this.$page.props.auth.user.id ||
                this.$isAdmin ||
                this.$isModerator
            )
        },
    },

    methods: {
        handleEnterEdit(event) {
            if (!event.shiftKey) {
                this.updateComment()
            }
        },

        handleEnterPress(event) {
            if (!event.shiftKey) {
                this.addReply()
            }
        },

        addReply() {
            if (this.canReply) {
                this.savingReply = true
                this.errors = null
                axios
                    .post(this.route('api.interaction.comment.create'), {
                        model_class: 'interaction',
                        model_id: this.comment.id,
                        comment: this.commentBody,
                    })
                    .then((response) => {
                        this.toggleReplying()
                        this.commentBody = ''
                        this.childComments.unshift(response.data)
                    })
                    .catch((error) => {
                        this.errors = error.response.data.errors
                    })
                    .finally(() => {
                        this.savingReply = false
                    })
            }
        },

        updateComment() {
            if (this.canUpdate) {
                this.updating = true
                axios
                    .patch(
                        this.route('api.interaction.comment.update', {
                            interaction: this.comment.id,
                        }),
                        {
                            comment: this.commentEditValue,
                        }
                    )
                    .then(({ data }) => {
                        this.updating = false
                        this.comment = data
                        this.toggleEditing()
                    })
                    .catch((error) => {
                        this.updating = false
                        this.toggleEditing()
                    })
            }
        },

        deleteComment() {
            this.$refs.confirmDanger
                .confirm({
                    title: this.$lang.get('Delete :definitive_item_name', {
                        definitive_item_name: this.$lang.get('Comment'),
                    }),
                    body:
                        this.$lang.get(
                            'Are you sure you want to delete :definitive_item_name?',
                            {
                                definitive_item_name:
                                    this.$lang.get('this comment'),
                            }
                        ) +
                        '<br>' +
                        this.$lang.get(
                            'If the comment has replies, then they will be preserved and this comment will be empty without author and date'
                        ),
                    confirmText: this.$lang.get('Delete'),
                })
                .then((confirmed) => {
                    if (confirmed) {
                        axios
                            .delete(
                                route('api.interaction.comment.destroy', {
                                    interaction: this.comment.id,
                                })
                            )
                            .then(({ data }) => {
                                if (data.soft_deleted) {
                                    this.setToSoftDeleted()
                                } else {
                                    this.$emit('removed', this.comment.id)
                                }
                            })
                    }
                })
        },

        /**
         * Fetch a single comment that will be added to the childComments array
         * This is triggered when another user creates a comment
         * @param commentId
         */
        fetchSingleComment(commentId) {
            axios
                .get(
                    route('api.interaction.comment.show', {
                        interaction: commentId,
                    })
                )
                .then((response) => {
                    this.childComments.unshift(response.data)
                })
        },

        /**
         * Refreshes the content of the comment
         * This is triggered when another user updates the comment
         */
        refreshComment() {
            axios
                .get(
                    route('api.interaction.comment.show', {
                        interaction: this.comment.id,
                    })
                )
                .then(({ data }) => {
                    this.comment.has_children = data.has_children
                    this.comment.updated_at = data.updated_at
                    this.comment.notes = data.notes
                })
        },

        initEcho() {
            if (this.$page.props.auth.user) {
                this.echoChannel = window.Echo.private(
                    `${this.modelClass}-${this.modelId}-interactions`
                )

                this.echoChannel.listenForWhisper('typing', (event) => {
                    if (event.commentId == this.comment.id) {
                        this.handleWhisper(event)
                    }
                })

                this.echoChannel.listen('CommentCreatedEvent', (event) => {
                    if (event.parentId == this.comment.id) {
                        this.fetchSingleComment(event.commentId)
                        this.checkIfTypingUserPosted(event.user.id)
                    }
                })

                this.echoChannel.listen('CommentUpdatedEvent', (event) => {
                    if (event.commentId == this.comment.id) {
                        this.refreshComment()
                    }
                })

                this.echoChannel.listen('CommentSoftDeletedEvent', (event) => {
                    if (event.commentId == this.comment.id) {
                        this.setToSoftDeleted()
                    }
                })

                this.echoChannel.listen('CommentDeletedEvent', (event) => {
                    if (event.parentId == this.comment.id) {
                        this.childCommentRemoved(event.commentId)
                    }
                })
            }
        },

        destroyEcho() {
            if (this.$page.props.auth.user) {
                window.Echo.leaveChannel(
                    `${this.modelClass}-${this.modelId}-interactions`
                )
                this.echoChannel = null
            }
        },

        toggleReplying() {
            this.replying = !this.replying
            if (this.replying) {
                setTimeout(() => {
                    this.$refs.textareaReply.focusMethod()
                }, 200)
            }
        },

        toggleEditing() {
            this.editing = !this.editing
            if (this.editing) {
                this.commentEditValue = this.comment.notes
                setTimeout(() => {
                    this.$refs.textareaEdit.focusMethod()
                }, 300)
            } else {
                this.commentEditValue = ''
            }
        },

        setToSoftDeleted() {
            this.comment.deleted_at = Date.now()
            this.comment.notes = null
            this.comment.user = null
        },

        childCommentRemoved(commentId) {
            this.childComments = this.childComments.filter(
                (comment) => comment.id != commentId
            )
        },
    },
}
</script>
